import styled from "@emotion/styled";
import { Box, IconButton } from "@mui/material";
import { useScroll } from "contexts/ScrollingContext";
import { useScrollMinMax } from "pazChargeComponents/general/ScrollUpButton";
import { JSXElementConstructor, ReactElement, useEffect, useRef } from "react";
import { trackScrollerClick } from "reusable/ChargeGTM";
import colors from "theme/colors";
import { Desktop, NewText, useIsDesktop } from "ui";
import { useKeyboardNavigation } from "utils";

const StyledFixed = styled(Box, {
  shouldForwardProp: (prop) =>
    !["noSectionsToShow", "smallDesktopMaxWidth"].includes(prop),
}) <{ noSectionsToShow: boolean, smallDesktopMaxWidth: number }>`
  ${(props) => props.theme.breakpoints.up("md")} {
    position: fixed;
    top: 142px;
    left: 0px;
    padding: 36px 0px;
    padding-left: 16px;
    width: 220px;
    display: ${(props) => props.noSectionsToShow ? "none" : "flex"};
    flex-direction: column;
    align-items: start;
    gap: 20px;
    border-left: 16px solid ${(props) => props.theme.colors.yellow};
    box-sizing: border-box;
    z-index: 100;
    padding-left: 11px;
    border-left: 10px solid ${(props) => props.theme.colors.yellow};
    max-width: ${(props) => props.smallDesktopMaxWidth}px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-left: 16px;
    border-left: 16px solid ${(props) => props.theme.colors.yellow};
    max-width: 200px;
  }
  ${(props) => props.theme.breakpoints.down("md")} {
    display: none;
  }
`;

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    !["isSectionSelected"].includes(prop),
}) <{ isSectionSelected: boolean; }>`
  padding: 0px;
  text-align: start;
  :hover {
    background-color: none;
  }
  :focus-visible{
    border:1px solid ${colors.gray};
  }
  border-radius: 0px;
  ${(props) => props.theme.breakpoints.down("md")} {
    height: 100%;
    border-bottom: ${(props) => (props.isSectionSelected ? `4px solid ${props.theme.colors.yellow}` : 'none')};
  }
`;

interface DesktopScrollerProps {
  smallDesktopMaxWidth: number;
  pageName?: string;
  minOffsetShow: number;
}

const ScrollDesktop: React.FC<DesktopScrollerProps> = ({
  smallDesktopMaxWidth,
  pageName,
  minOffsetShow,
}) => {

  const { activeSection, children, scrollToSection, focusSection } = useScroll()
  const isDesktop = useIsDesktop();
  const isKeyNavigation = useKeyboardNavigation()

  const [showScroller] = useScrollMinMax(minOffsetShow, 1000, minOffsetShow === 0);
  const ref = useRef<HTMLDivElement>(null);
  const desktopButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const handleClick = (child: ReactElement<any, string | JSXElementConstructor<any>>) => {
    scrollToSection(child.props.id);
    trackScrollerClick(isDesktop ? "beneift" : "mobile_navigation_home_page", child.props.id, pageName || "Generic Page");
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (!showScroller) { return }
    event.preventDefault();
    event.stopPropagation();

    const currentIndex = index
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        newIndex = currentIndex > 0 ? currentIndex - 1 : children.length - 1;


        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        newIndex = currentIndex < children.length - 1 ? currentIndex + 1 : 0;

        break;
      case 'Tab':
        event.preventDefault();
        if (activeSection) {
          focusSection(activeSection);
        }
        return;
      default:
        return;
    }
    const newActiveSection = children[newIndex].props.id;
    scrollToSection(newActiveSection);
    // focus on button
    desktopButtonRefs.current[newActiveSection]?.focus();

    trackScrollerClick(isDesktop ? "benefit" : "mobile_navigation_home_page", newActiveSection, pageName || "Generic Page");
  }


  useEffect(() => {
    if (showScroller && ref && isKeyNavigation) {
      desktopButtonRefs.current[children[0].props.id]?.focus();
    }
  }, [showScroller])

  return (
    <Desktop>
      <StyledFixed
        noSectionsToShow={children.length === 0 || !showScroller}
        smallDesktopMaxWidth={smallDesktopMaxWidth}
        ref={ref}
        tabIndex={-1}
        role="tablist"
        aria-orientation="vertical"
        aria-label="תפריט ניווט בעמוד"
      >
        {children?.map((child, index) => (
          <StyledIconButton
            style={{ backgroundColor: "transparent", fontFamily: "inherit" }}
            key={index}
            onClick={() => handleClick(child)}
            isSectionSelected={activeSection === child.props.id}
            ref={(el) => {
              desktopButtonRefs.current[child.props.id] = el;
            }}
            tabIndex={activeSection === child.props.id ? 0 : -1}
            role="tab"
            aria-selected={activeSection === child.props.id}
            aria-controls={child.props.id}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="scroller-button"
          >
            <NewText
              desktopFontSize={activeSection === child.props.id ? 18 : 16}
              largeDesktopFontSize={activeSection === child.props.id ? 20 : 16}
              largeDesktopLineHeight={
                activeSection === child.props.id ? "28px" : "22.4px"
              }
              mobileFontSize={16}
              component={"p"}
              desktopFontWeight={
                activeSection === child.props.id ? "600" : "400"
              }
              desktopLineHeight={
                activeSection === child.props.id ? "19.6px" : "16.8px"
              }
            >
              {child.props.id}
            </NewText>
          </StyledIconButton>
        ))}
      </StyledFixed>
    </Desktop>
  );
};

export default ScrollDesktop;
